import { Box, Typography } from "@mui/material";
import { ReactNode, useEffect, useId, useRef } from "react";
import { FieldLoader } from "./FieldLoader";

interface Props {
  label?: string;
  hint?: string;
  error?: string;
  optionalSuffix?: string;
  /**
   * Opt-in: marca el campo como obligatorio con un asterisco junto al label (QA PRA-08). El input debe llevar además
   * `required` y `aria-required`; el asterisco es solo visual (`aria-hidden`).
   */
  required?: boolean;
  full?: boolean;
  /**
   * Sobrepone un spinner al campo mientras carga su catálogo o consulta (ver `FieldLoader`).
   * Pasarlo o no debe ser estático por call site: la discriminación es por `undefined`, para que la
   * estructura del árbol no cambie en runtime y el campo no se remonte (perdería el foco).
   */
  loading?: boolean;
  children: ReactNode;
}

/**
 * Controles del campo que anuncia el lector de pantalla: inputs y textareas visibles y el botón de los selects de
 * MUI. Fuera quedan el input oculto del select (`aria-hidden`) y los `type="hidden"`.
 */
const CONTROLS_SELECTOR =
  "input:not([type='hidden']):not([aria-hidden='true']), textarea:not([aria-hidden='true']), [role='combobox'], [role='button'][aria-haspopup='listbox']";

export const FieldWrapper = ({ label, hint, error, optionalSuffix, required, full, loading, children }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const messageId = `${useId()}-mensaje`;
  const message = error || hint;

  // Accesibilidad del error (QA PRA-09): con error, el control lleva `aria-invalid="true"` y el mensaje ligado con
  // `aria-describedby`; sin error, `aria-invalid="false"` y, si hay ayuda, la ayuda ligada. Se aplica sobre el DOM
  // porque el control lo pasa cada pantalla como `children` (TextField, Select, Autocomplete…) y así vale para
  // todos sin tocar cada uno. Corre en cada render para cubrir controles que se montan después.
  useEffect(() => {
    ref.current?.querySelectorAll<HTMLElement>(CONTROLS_SELECTOR).forEach((control) => {
      control.setAttribute("aria-invalid", error ? "true" : "false");
      if (message) {
        control.setAttribute("aria-describedby", messageId);
      } else if (control.getAttribute("aria-describedby") === messageId) {
        control.removeAttribute("aria-describedby");
      }
    });
  });

  return (
    <Box ref={ref} sx={{ gridColumn: full ? "1 / -1" : undefined, display: "flex", flexDirection: "column", gap: 0.5 }}>
      {label && (
        <Typography variant="body2" fontWeight={500} color="primary.dark">
          {label}
          {required && (
            <Typography component="span" variant="body2" color="error.main" aria-hidden sx={{ ml: 0.25 }}>
              *
            </Typography>
          )}
          {optionalSuffix && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
              {optionalSuffix}
            </Typography>
          )}
        </Typography>
      )}
      {loading === undefined ? children : <FieldLoader loading={loading}>{children}</FieldLoader>}
      {error ? (
        <Typography id={messageId} variant="body2" color="error.main" role="alert">
          {error}
        </Typography>
      ) : hint ? (
        <Typography id={messageId} variant="body2" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
};

/**
 * `SelectProps` que oculta el chevron del desplegable mientras el campo carga su catálogo, para que
 * no se encime con el spinner de `loading`. Va en el `TextField`, no en el wrapper.
 */
// eslint-disable-next-line react-refresh/only-export-components -- helper del propio campo; conviene tenerlo junto al wrapper
export const selectLoadingProps = (loading: boolean) => (loading ? { IconComponent: () => null } : undefined);
